import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import * as Dialog from '@studio/design-system/dialog';
import cameraImg from './images/camera.png';
import { config } from '#~/config/config.js';
import { isElectronApp } from '#~/electron/utils.js';
import { useAuth0 } from '#~/auth/index.js';
import { usePrefState } from '#~/preferences/hooks.js';
import { useAnalytics } from '#~/analytics/index.js';
import {
  createPermissionsLifecycleTracker,
  createPermissionsModalInstanceId
} from '#~/analytics/domains/index.js';
import { Anchor, Button } from '@studio/design-system/button';
import { Callout } from '@studio/design-system/callout';
import { Checkbox } from '@studio/design-system/checkbox';
import { Icon } from '@studio/design-system/icon';
import { Centered } from '@studio/design-system/overlay';
import { Spinner } from '@studio/design-system/spinner';
import T from '@studio/design-system/themes';
import { Vendor, getCurrentVendor } from '@studio/design-system/themes/vendor.js';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement, ReactNode } from 'react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { create } from 'zustand';
import networkImg from './images/network.svg';
import startupImg from './images/startup.png';
import { shouldSuppressPermissionsPrompt, useInitialPermissionsCheck } from './prompt-gate.js';
import { usePermissions, type PermissionState } from './use-permissions.js';
import { useOnboardingPermissionsClaim } from '#~/onboarding/store.js';
import { useGrantSuccessTracking } from './use-grant-success-tracking.js';

const vendorPrefixMap: { [key in Vendor]: string } = {
  [Vendor.IRIS]: 'Iris',
  [Vendor.PTZOPTICS]: 'Hive'
};

const usePermissionsModalCount = (userId?: string) => {
  const key = userId ? `permissions.modal.count.${userId}` : 'permissions.modal.count.__none__';
  const [raw, setRaw] = usePrefState<string>(key, '0');
  const count = Number(raw) || 0;

  const increment = (): number => {
    const next = count + 1;
    setRaw(String(next));
    return next;
  };

  return { count, increment };
};

export const usePermissionsStore = create<{
  skip: boolean;
  setSkip: (show: boolean) => void;
}>()((set) => ({
  skip: window.localStorage.getItem('skip-permissions') === 'true',
  setSkip: (skip) => {
    (set({ skip }), window.localStorage.setItem('skip-permissions', skip ? 'true' : 'false'));
  }
}));

interface PermissionEntry {
  key: keyof ReturnType<typeof usePermissions>['permissions'];
  icon: ReactElement;
  title: string;
  description: string;
  onAllow?: () => void | Promise<unknown>;
  onDisable?: () => void | Promise<unknown>;
  alwaysShowAllow?: boolean;
  loading: boolean;
  permission?: PermissionState;
  settings?: ReactNode;
  continueCondition: 'canIgnore' | 'mustInteract' | 'mustAccept';
}

export const useRequirePermissions = ({
  title,
  description,
  required
}: {
  title: string;
  description: string;
  required: PermissionEntry['key'][];
}) => {
  const { open, close } = Dialog.useDialog(RawPermissionsModal);
  const { track } = useAnalytics();
  const permissionsTracker = useMemo(() => createPermissionsLifecycleTracker(track), [track]);
  const { user } = useAuth0();
  const userId = user?.sub ?? user?.id;
  const { skip } = usePermissionsStore();
  const { increment: incrementModalCount } = usePermissionsModalCount(userId);
  const {
    permissions: { camera, microphone, network, startup }
  } = usePermissions();

  const corePermissions = [camera, microphone, network].filter(
    (v) => required.includes(v.key) && v.enabled
  );

  const requiredPermissions = [camera, microphone, network, startup].filter(
    (v) => required.includes(v.key) && v.enabled
  );

  const allLoaded = requiredPermissions.every((v) => !v.isLoading);
  const allAllowed = requiredPermissions.every((v) => v.permissionState === 'allowed');

  const coreLoaded = corePermissions.every((v) => !v.isLoading);
  const coreAllowed = corePermissions.every((v) => v.permissionState === 'allowed');

  const [hasShown, setShown] = usePrefState<string>('room.shown-permissions-modal', 'false');

  const initialCheck = useRef(true);

  useEffect(() => {
    if (
      shouldSuppressPermissionsPrompt({
        skip,
        isElectron: isElectronApp(),
        noMediaInputSandbox: config.noMediaInputSandbox
      }) ||
      !initialCheck.current
    ) {
      return;
    }

    if (allLoaded && !allAllowed) {
      if (coreLoaded && coreAllowed && hasShown === 'true') {
        return;
      }

      setShown('true');
      const permissionsModalCount = incrementModalCount();
      const modalInstanceId = createPermissionsModalInstanceId();

      permissionsTracker.trackOpened(
        {
          modal_instance_id: modalInstanceId,
          surface: 'required_permissions',
          permissions_modal_count: permissionsModalCount
        },
        {
          required_permissions: requiredPermissions
            .filter((v) => v.state !== 'allowed')
            .map((v) => v.key)
            .join(',')
        }
      );

      initialCheck.current = false;
      open({
        title,
        description,
        requiredPermissions: requiredPermissions
          .filter((v) => v.state !== 'allowed')
          .map((v) => v.key),
        canSkip: true,
        onSkip: close,
        onContinue: close,
        modalInstanceId,
        surface: 'required_permissions'
      });
    }
  }, [
    allLoaded,
    allAllowed,
    close,
    coreAllowed,
    coreLoaded,
    description,
    hasShown,
    open,
    requiredPermissions,
    setShown,
    skip,
    title,
    track,
    permissionsTracker,
    userId
  ]);

  return null;
};

// Share error recovery across the wizard and standalone ask without treating an unknown OS as allowed.
export const PermissionsOSRecovery = ({
  onRetry,
  retrying
}: {
  onRetry: () => void;
  retrying: boolean;
}) => (
  <Dialog.Root open>
    <Dialog.Overlay className='z-[500]'>
      <Centered>
        <Dialog.Content {...DataSTag(Sel.Permissions, 'os-error')} aria-describedby={undefined}>
          <div
            className='w-[570px] rounded-12 bg-white p-40 text-center text-black'
            data-theme={T.getTheme('light')}
          >
            <Dialog.Title className='text-xl'>Couldn't check your device permissions</Dialog.Title>
            <p className='mt-12 text-base'>Try again to finish setting up your Studio.</p>
            <Button
              {...STagNew(Sel.Permissions, 'retry-os')}
              className='focus-visible:ring-primary/50 mt-24 focus-visible:ring-2'
              disabled={retrying}
              onClick={onRetry}
            >
              {retrying && <Spinner />}
              Try again
            </Button>
          </div>
        </Dialog.Content>
      </Centered>
    </Dialog.Overlay>
  </Dialog.Root>
);

export const OnboardPermissionsModal = () => {
  if (config.noMediaInputSandbox) return null;

  // Stand down while the first-run funnel owns the ask (ENG-3796). Both are
  // modals on `/:roomId`, so without this the standalone dialog opens
  // UNDERNEATH the funnel where the user cannot reach it. Leaf import, NOT
  // '#~/onboarding/index.js': index re-exports the gate, whose import graph
  // reaches a Vite `?worker` module jest cannot link.
  const funnelOwnsPermissions = useOnboardingPermissionsClaim((s) => s.funnelOwnsPermissions);

  const answered = useInitialPermissionsCheck((s) => s.answered);
  const markComplete = useInitialPermissionsCheck((s) => s.markComplete);
  const finishPrompt = useInitialPermissionsCheck((s) => s.finishPrompt);
  const { open, close } = Dialog.useDialog(RawPermissionsModal);
  const { track } = useAnalytics();
  const permissionsTracker = useMemo(() => createPermissionsLifecycleTracker(track), [track]);
  const { user } = useAuth0();
  const userId = user?.sub ?? user?.id;
  const { increment: incrementModalCount } = usePermissionsModalCount(userId);

  const {
    isOSKnown,
    osError,
    isOSFetching,
    retryOS,
    permissions: { camera, microphone, network, startup }
  } = usePermissions();

  const corePermissions = [camera, microphone, network].filter((v) => v.enabled);

  const requiredPermissions = [camera, microphone, network, startup].filter((v) => v.enabled);
  const allLoaded = requiredPermissions.every((v) => !v.isLoading);
  const allAllowed = requiredPermissions.every((v) => v.permissionState === 'allowed');

  const coreLoaded = corePermissions.every((v) => !v.isLoading);
  const coreAllowed = corePermissions.every((v) => v.permissionState === 'allowed');

  const [hasShown, setShown] = usePrefState<string>('room.shown-permissions-modal', 'false');

  const initialCheck = useRef(true);

  useEffect(
    () => () => {
      // The shared dialog removes an unmounted owner's prompt. Release only our own latch
      // so returning can reopen an unanswered ask; interruption must not complete permissions.
      if (!initialCheck.current) {
        // @ts-ignore
        window.irisPermissionModalShown = false;
      }
    },
    []
  );

  const safeClose = useLatestFn(() => {
    finishPrompt();
    close();
  });

  useEffect(() => {
    // Finish the initial check only after native status is known or the user answers the prompt.
    // The funnel owns its own terminal decision, including denial, so do not ask again afterward.
    if (funnelOwnsPermissions || answered || !isOSKnown || !allLoaded) return;
    if (allAllowed || (coreLoaded && coreAllowed && hasShown === 'true')) {
      markComplete();
      return;
    }
    if (!allAllowed) {
      // @ts-ignore
      if (window.irisPermissionModalShown) {
        return;
      }

      setShown('true');
      const permissionsModalCount = incrementModalCount();
      const modalInstanceId = createPermissionsModalInstanceId();

      permissionsTracker.trackOpened({
        modal_instance_id: modalInstanceId,
        surface: 'onboard_permissions',
        permissions_modal_count: permissionsModalCount
      });
      // @ts-ignore
      window.irisPermissionModalShown = true;
      initialCheck.current = false;

      open?.({
        title: "Let's get your Studio setup!",
        description: 'We need your permission to enable all features',
        onContinue: safeClose,
        onSkip: safeClose,
        modalInstanceId,
        surface: 'onboard_permissions'
      });
    }
  }, [
    allAllowed,
    allLoaded,
    answered,
    isOSKnown,
    markComplete,
    coreAllowed,
    coreLoaded,
    funnelOwnsPermissions,
    hasShown,
    open,
    safeClose,
    setShown,
    track,
    permissionsTracker,
    userId
  ]);

  return !funnelOwnsPermissions && !answered && osError ? (
    <PermissionsOSRecovery
      retrying={isOSFetching}
      onRetry={() => {
        void retryOS();
      }}
    />
  ) : null;
};

export const RawPermissionsModal = forwardRef<
  Dialog.Api,
  {
    title: string;
    description: string;
    requiredPermissions?: PermissionEntry['key'][];
    canSkip?: boolean;
    modalInstanceId: string;
    surface: string;

    onContinue?: () => void;
    onSkip?: () => void;
  }
>(
  (
    {
      requiredPermissions,
      onContinue,
      onSkip,
      canSkip,
      title,
      description,
      modalInstanceId,
      surface
    },
    forwardedRef
  ) => {
    const { permissions: permissionsMap } = usePermissions();
    const { track } = useAnalytics();
    const permissionsTracker = useMemo(() => createPermissionsLifecycleTracker(track), [track]);
    const { user } = useAuth0();
    const userId = user?.sub ?? user?.id;
    const { count: permissionsModalCount } = usePermissionsModalCount(userId);
    const lifecycleContext = useMemo(
      () => ({
        modal_instance_id: modalInstanceId,
        surface,
        permissions_modal_count: permissionsModalCount
      }),
      [modalInstanceId, permissionsModalCount, surface]
    );
    const hasTrackedCloseRef = useRef(false);
    // Add-only latch: a key is recorded the first time it reads `denied` so the failure emits
    // once per permission. ENG-4420: this Set is only safe as add-only (never retracted)
    // because the settled-gate in use-permissions.ts (`derivePermissionState`) guarantees a
    // pending grant never reads `denied` in the first place — so no false denial can enter
    // here to need retraction. Prevention-at-source beats post-hoc correction.
    const deniedPermissionKeysRef = useRef<Set<string>>(new Set());
    const { camera, microphone, network, startup } = permissionsMap;

    const vendor = getCurrentVendor() ?? Vendor.IRIS;
    const vendorVariant = vendorPrefixMap[vendor];

    const permissions: PermissionEntry[] = (
      [
        {
          key: 'network',
          title: 'Network',
          icon: <img src={networkImg} width={40} alt='Network permission icon' />,
          description: `Allows ${vendorVariant} to access and connect with local devices on your network.`,
          onAllow: network.mutate,
          alwaysShowAllow: true,
          loading: network.isLoading,
          permission: network.state,
          continueCondition: 'mustAccept',
          settings: (
            <Callout
              className='-mt-20'
              severity='error'
              title={
                <p className='text-sm font-medium'>
                  Permission is required. Please press{' '}
                  <Anchor
                    className='text-primary hover:text-primary-highlight'
                    onClick={() => {
                      void network.mutate();
                    }}
                  >
                    allow
                  </Anchor>{' '}
                  again to accept.
                </p>
              }
            />
          )
        },
        {
          key: 'camera',
          title: 'Camera',
          icon: <img src={cameraImg} width={40} alt='Camera permission icon' />,
          description: `Allows ${vendorVariant} to access USB and built-in cameras (webcams, capture cards, etc).`,
          onAllow: camera.mutate,
          loading: camera.isLoading,
          permission: camera.state,
          continueCondition: 'mustInteract',
          settings: (
            <Callout
              className='-mt-20'
              severity='error'
              title={
                <p className='text-sm font-medium'>
                  Permission was denied. You must go to{' '}
                  <Anchor
                    className='text-primary hover:text-primary-highlight'
                    onClick={camera.openSettings}
                  >
                    settings
                  </Anchor>{' '}
                  in order to enable permission.
                </p>
              }
            />
          )
        },
        {
          key: 'microphone',
          title: 'Microphone',
          icon: <Icon icon='audio' className='text-[40px]' />,
          description: `Allows ${vendorVariant} to record or capture audio from built-in audio sources.`,
          onAllow: microphone.mutate,
          loading: microphone.isLoading,
          permission: microphone.state,
          continueCondition: 'mustInteract',
          settings: (
            <Callout
              className='-mt-20'
              severity='error'
              title={
                <p className='text-sm font-medium'>
                  Permission was denied. You must go to{' '}
                  <Anchor
                    className='text-primary hover:text-primary-highlight'
                    onClick={microphone.openSettings}
                  >
                    settings
                  </Anchor>{' '}
                  in order to enable permission.
                </p>
              }
            />
          )
        },
        {
          key: 'startup',
          title: 'Application Startup',
          icon: <img src={startupImg} width={32} alt='Startup permission icon' />,
          description: `Allows ${vendorVariant} to launch at system startup.`,
          onAllow: () => startup.mutate(true),
          loading: startup.isLoading,
          permission: startup.state,
          continueCondition: 'canIgnore'
        }
      ] satisfies PermissionEntry[]
    ).filter(
      (v) =>
        permissionsMap[v.key].enabled &&
        (requiredPermissions ? requiredPermissions.includes(v.key) : true)
    );

    const allRequiredPermissionsAllowed = permissions.every((v) =>
      requiredPermissions
        ? v.permission === 'allowed'
        : v.continueCondition === 'canIgnore'
          ? true
          : v.continueCondition === 'mustInteract'
            ? v.permission !== 'default'
            : v.continueCondition === 'mustAccept'
              ? v.permission === 'allowed'
              : false
    );

    const { setSkip: persistSetSkip } = usePermissionsStore();
    const [skip, setSkip] = useState(false);

    const trackClosedOnce = (properties: Record<string, unknown> = {}) => {
      if (hasTrackedCloseRef.current) {
        return;
      }

      hasTrackedCloseRef.current = true;
      permissionsTracker.trackClosed(lifecycleContext, properties);
    };

    // Failure send-boundary. ENG-4420: `permission.permission === 'denied'` now only holds
    // for a settled refetch (see `derivePermissionState`), so this fires solely on real
    // denials — never during the post-grant pending window. Do NOT reintroduce a `denied`
    // read that is not settled-gated, or the false-failure regression returns.
    useEffect(() => {
      permissions.forEach((permission) => {
        if (
          permission.permission !== 'denied' ||
          deniedPermissionKeysRef.current.has(permission.key)
        ) {
          return;
        }

        deniedPermissionKeysRef.current.add(permission.key);
        permissionsTracker.trackError(lifecycleContext, {
          permission_key: permission.key,
          error_message: 'permission_denied'
        });
      });
    }, [permissions, permissionsTracker, lifecycleContext]);

    // Net-new canonical success terminal (ENG-4307, R-perms). Fires exactly once per modal
    // instance, only on a real in-session transition into the STRICT all-required-`allowed`
    // state — deliberately NOT the loose `allRequiredPermissionsAllowed` continue-gate,
    // which is true on a `mustInteract` denial and at mount for a returning user (both would
    // be false successes). Per rule: reference-rules-in-comments (ai.knowledge/global) —
    // the strict predicate + transition guards live in useGrantSuccessTracking.
    const trackGrantSucceeded = useLatestFn(() => {
      permissionsTracker.trackGrantSucceeded(lifecycleContext);
    });
    useGrantSuccessTracking(permissions, trackGrantSucceeded);

    const handlePermissionAllow = (
      key: PermissionEntry['key'],
      action: 'allow' | 'retry_allow',
      onAllow?: () => void | Promise<unknown>
    ) => {
      permissionsTracker.trackPermissionAction(lifecycleContext, {
        permission_key: key,
        permission_action: action
      });

      try {
        const result = onAllow?.();

        if (result && typeof (result as Promise<unknown>).then === 'function') {
          void (result as Promise<unknown>).catch((error) => {
            permissionsTracker.trackError(lifecycleContext, {
              permission_key: key,
              error_message: error instanceof Error ? error.message : String(error)
            });
          });
        }
      } catch (error) {
        permissionsTracker.trackError(lifecycleContext, {
          permission_key: key,
          error_message: error instanceof Error ? error.message : String(error)
        });
      }
    };

    return (
      <Dialog.Root
        ref={forwardedRef}
        onOpenChange={(open) => {
          if (!open) {
            trackClosedOnce({ close_reason: 'dismissed' });
          }
        }}
      >
        <Dialog.Overlay className='z-[500]'>
          <Centered>
            <Dialog.Content {...DataSTag(Sel.Permissions, 'dialog')}>
              <div
                className='relative z-10 w-[570px] rounded-12 bg-white text-black'
                data-theme={T.getTheme('light')}
              >
                <div className='p-40 text-center'>
                  <h2 className='text-xl'>{title}</h2>
                  <p className='text-base font-regular'>{description}</p>
                </div>
                <div className='flex flex-col gap-[32px] border-t-1 border-light p-40'>
                  {permissions.map(
                    ({
                      key,
                      icon,
                      title,
                      onAllow,
                      alwaysShowAllow,
                      onDisable,
                      description,
                      permission,
                      loading,
                      settings
                    }) => (
                      <div key={title}>
                        <div className='flex justify-between gap-20'>
                          <div className='flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full bg-[#DFE4EF]'>
                            {icon}
                          </div>
                          <div className='flex flex-grow flex-col gap-4'>
                            <div className='text-lg font-demibold'>{title}</div>
                            <p className='text-md font-medium text-[#2C344F]'>{description}</p>
                          </div>
                          <div className='self-center'>
                            {permission === 'allowed' ? (
                              <Button className='w-[55px]' onClick={onDisable}>
                                <i className='i-mdi-tick text-[20px]' />
                              </Button>
                            ) : permission === 'default' || alwaysShowAllow || loading ? (
                              <Button
                                {...STagNew(Sel.Permissions, 'allow', key)}
                                variant='secondary'
                                disabled={loading}
                                className='flex items-center gap-6'
                                onClick={() => {
                                  handlePermissionAllow(key, 'allow', onAllow);
                                }}
                              >
                                {loading && <Spinner />}
                                <div>Allow</div>
                              </Button>
                            ) : (
                              <Button
                                variant='danger'
                                onClick={() => {
                                  handlePermissionAllow(key, 'retry_allow', onAllow);
                                }}
                                className='w-[55px]'
                              >
                                <i className='i-mdi-close text-[20px]' />
                              </Button>
                            )}
                          </div>
                        </div>
                        {permission === 'denied' && settings && (
                          <div className='mt-32'>{settings}</div>
                        )}
                      </div>
                    )
                  )}
                </div>
                <div className='flex flex-col items-center gap-16 border-t-1 border-light p-40'>
                  <Button
                    {...STagNew(Sel.Permissions, 'continue')}
                    size='xl'
                    className='w-[256px]'
                    onClick={() => {
                      if (canSkip) {
                        if (skip) {
                          persistSetSkip(true);
                        }

                        if (allRequiredPermissionsAllowed) {
                          permissionsTracker.trackContinue(lifecycleContext);
                          trackClosedOnce({ close_reason: 'continue' });
                        } else {
                          permissionsTracker.trackSkip(lifecycleContext);
                          trackClosedOnce({ close_reason: 'skip' });
                        }
                        onSkip?.();
                      } else {
                        permissionsTracker.trackContinue(lifecycleContext);
                        trackClosedOnce({ close_reason: 'continue' });
                        onContinue?.();
                      }
                    }}
                    disabled={canSkip ? false : !allRequiredPermissionsAllowed}
                  >
                    {canSkip ? (allRequiredPermissionsAllowed ? 'Continue' : 'Skip') : 'Continue'}
                  </Button>
                  {canSkip && !allRequiredPermissionsAllowed && (
                    <div className='flex items-center gap-6'>
                      <Checkbox.Root
                        onCheckedChange={(value) => {
                          setSkip(value === true);
                        }}
                        id='skip-permissions'
                        className='h-16 w-16 rounded-2 border-1 border-[#A9B3C6]'
                      >
                        <Checkbox.Indicator>
                          <i className='i-mdi-tick' />
                        </Checkbox.Indicator>
                      </Checkbox.Root>
                      <label htmlFor='skip-permissions' className='text-sm text-foreground'>
                        Don't show me this again
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </Dialog.Content>
          </Centered>
        </Dialog.Overlay>
      </Dialog.Root>
    );
  }
);
